import type { FC } from "react";
import { useCallback, useEffect } from "react";

import { useTranslations } from "@ui/providers/useTranslations";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import { usePolling } from "@jti/rest-api";

import styles from "./AsyncValidationPopup.module.scss";

export type AsyncValidationPopupProps = {
  isOpened?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
  onTryAgain?: () => void;
  onCancel?: () => void;
};

export const AsyncValidationPopup: FC<AsyncValidationPopupProps> = ({
  onTryAgain,
  onCancel,
  onRefresh,
  isOpened,
  isError,
}) => {
  const { __ } = useTranslations();
  const { startPolling, stopPolling } = usePolling();

  const mainInformationText = isError
    ? __("ageverification.async_popup.error_message")
    : __("ageverification.async_popup.description");

  const handlePollingAvData = useCallback(() => {
    if (!isOpened || isError) {
      stopPolling();
      return;
    }
    stopPolling(); // clear previous interval to not loose reference to it
    if (onRefresh) startPolling(onRefresh, 5000);
  }, [isError, isOpened, onRefresh, startPolling, stopPolling]);

  useEffect(() => {
    handlePollingAvData();
    return () => stopPolling();
  }, [handlePollingAvData, stopPolling]);

  if (!isOpened) {
    return null;
  }

  return (
    <div className={styles.popupWrapper} data-testid="async-av-validation-popup">
      <div className={styles.popupContainer}>
        <div className={styles.headline}>{__("ageverification.async_popup.headline")}</div>
        <UiCrossIcon onClick={onCancel} className={styles.closeIcon} width="15px" height="15px" />
        <p className={styles.popupDescription}>{mainInformationText}</p>
        {!isError && <Loader data-testid="async-av-validation-popup-loading" />}
        <div className={styles.popupActionBar}>
          <Button className={styles.actionButton} variant="secondary" onClick={onCancel} disabled={!isError}>
            {__("ageverification.async_popup.button.cancel")}
          </Button>
          <Button className={styles.actionButton} variant="primary" onClick={onTryAgain} pending={!isError}>
            {__("ageverification.async_popup.button.try_again")}
          </Button>
        </div>
      </div>
    </div>
  );
};
